import { ApplicationElement, html } from '@crossiety/lit-components';
import BaseMenu from '@crossiety/lit-components/components/base_menu/element.js';

import '@crossiety/lit-components/components/icon_block/element.js';
import '@crossiety/lit-components/components/dot_menu/element.js';

import style from './style.lit.css';
import translations from './translations.js';

const FACEBOOK = 'Facebook';
const TWITTER = 'Twitter';
const WHATSAPP = 'WhatsApp';

/**
 * Social share menu: Facebook, Twitter, Whatsapp
 *
 * Renders icon only on mobile because we are calling native share
 *
 * @prop url - Url for share
 * @prop title - Additional message / title in your share
 * @prop dropdownMenu - if false it's hidden
 * @prop mobileExpanded - This will show multiple icons but click will call native share
 */

export class SocialShare extends BaseMenu {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    url: { type: String },
    title: { type: String },
    dropdownMenu: { type: Boolean },
    mobileExpanded: { type: Boolean },
  };

  static config = {
    icons: {
      facebook: 'facebook-f:brands',
      twitter: 'x-twitter:brands',
      whatsapp: 'whatsapp:brands',
      share: 'share-alt',
    },
    cssClasses: {
      trigger: 'trigger',
      mobileTrigger: 'mobile-share-btn',
    },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return this.url;
  }

  /** @internal */
  get shares() {
    return {
      twitter: [700, 300],
      facebook: [500, 500],
      other: [500, 500],
    };
  }

  /** @internal */
  get facebookUrl() {
    const share = encodeURIComponent(this.url);

    return `https://www.facebook.com/sharer/sharer.php?u=${share}`;
  }

  /** @internal */
  get twitterUrl() {
    const share = encodeURIComponent(
      `${this.title}: ${this.url}, via @beUnity_io`
    );

    return `https://twitter.com/intent/tweet?text=${share}`;
  }

  /** @internal */
  get whatsAppUrl() {
    const share = encodeURIComponent(`${this.title}: ${this.url}`);

    return `https://web.whatsapp.com/send?text=${share}`;
  }

  /** @internal */
  get isMobileApp() {
    return /client:(android|ios)/.test(navigator.userAgent);
  }

  /** @internal */
  get menuItems() {
    return [this.renderFacebook(), this.renderTwitter(), this.renderWhatsapp()];
  }

  /** @internal */
  get facebookItem() {
    return {
      title: this.t('actions.facebook'),
      action: this.actionFor('shareVisit', [this.facebookUrl, 'facebook']),
      icon: this.icon('facebook'),
      partName: 'facebook',
    };
  }

  /** @internal */
  get twitterItem() {
    return {
      title: this.t('actions.twitter'),
      action: this.actionFor('shareVisit', [this.twitterUrl, 'twitter']),
      icon: this.icon('twitter'),
      partName: 'twitter',
    };
  }

  /** @internal */
  get whatsAppItem() {
    return {
      title: this.t('actions.whatsapp'),
      action: this.actionFor('shareVisit', [this.whatsAppUrl, 'whatsapp']),
      icon: this.icon('whatsapp'),
      partName: 'whatsapp',
    };
  }

  /** @internal */
  get items() {
    return [this.facebookItem, this.twitterItem, this.whatsAppItem];
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.title,
      showCancel: true,
    };
  }

  icon(name) {
    return this.constructor.config.icons[name];
  }

  cssClass(name) {
    return this.constructor.config.cssClasses[name];
  }

  handleFacebookAction() {
    this.shareVisit(this.facebookUrl, 'facebook');
  }

  handleTwitterAction() {
    this.shareVisit(this.twitterUrl, 'twitter');
  }

  handleWhatsAppAction() {
    this.shareVisit(this.whatsAppUrl, 'whatsapp');
  }

  actionFor(method, args) {
    return {
      menu: 'xs-social-share',
      id: this.instanceId,
      method: method,
      args: args,
    };
  }

  shareVisit(url, popup) {
    const [width, height] = this.shares[popup] || this.shares.other;

    window.open(url, popup, `width=${width}, height=${height}`);
  }

  handleMobileClick() {
    this.dispatchEvent(
      new Event('socialShare-mobileClick', { bubbles: true, composed: true })
    );
  }

  renderItem(name) {
    const key = name.toLowerCase();

    return html`<ef-icon name="${this.icon(key)}"></ef-icon>`;
  }

  renderFacebook() {
    return html`
      <button
        class="share-btn facebook"
        @click=${this.handleFacebookAction}
        aria-label=${this.t('share_to', { name: 'Facebook' })}>
        ${this.renderItem(FACEBOOK)}
      </button>
    `;
  }

  renderTwitter() {
    return html`
      <button
        class="share-btn twitter"
        @click=${this.handleTwitterAction}
        aria-label=${this.t('share_to', { name: 'Twitter' })}>
        ${this.renderItem(TWITTER)}
      </button>
    `;
  }

  renderWhatsapp() {
    return html`
      <button
        class="share-btn whatsapp"
        @click=${this.handleWhatsAppAction}
        aria-label=${this.t('share_to', { name: 'WhatsApp' })}>
        ${this.renderItem(WHATSAPP)}
      </button>
    `;
  }

  renderIcon() {
    return html` <ef-icon name=${this.icon('share')}></ef-icon> `;
  }

  renderDropdownTrigger() {
    return html`
      <button
        slot="trigger"
        class=${this.cssClass('trigger')}
        aria-label=${this.t('share_options')}>
        ${this.renderIcon()}
      </button>
    `;
  }

  renderMobileShareButton() {
    return html`
      <button
        @click=${this.handleMobileClick}
        class="${this.cssClass('mobileTrigger')}"
        data-url=${this.url}
        aria-label=${this.t('share_options')}
        aria-haspopup="true"
        aria-expanded="false">
        ${this.renderIcon()}
      </button>
    `;
  }

  renderDropdown() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        ${this.renderDropdownTrigger()}
      </ef-dot-menu>
    `;
  }

  renderMobileShareButtonsExpanded() {
    return html`
      <button
        @click=${this.handleMobileClick}
        class="mobile-expanded-share-btn"
        data-url=${this.url}>
        <span class="options">
          ${this.renderItem(FACEBOOK)} ${this.renderItem(TWITTER)}
          ${this.renderItem(WHATSAPP)}
        </span>
      </button>
    `;
  }

  render() {
    if (this.isMobileApp && this.mobileExpanded) {
      return this.renderMobileShareButtonsExpanded();
    }

    if (this.isMobileApp) {
      return this.renderMobileShareButton();
    }

    if (this.dropdownMenu) {
      return this.renderDropdown();
    }

    return this.menuItems;
  }
}

export default SocialShare;

SocialShare.registerTagName('xs-social-share');
SocialShare.registerCommonActions('xs-social-share');

customElements.get('xs-social-share') ||
  customElements.define('xs-social-share', SocialShare);
